import React, { useState, useEffect } from 'react';
import './FirmJobs.css';
import { apiCall } from '../utils/api';

const AREA_OPTIONS = [
    'Audit', 'Tax', 'Advisory', 'Risk & Forensics',
    'Corporate Finance', 'Transfer Pricing', 'Tax Litigation',
    'Compliance', 'GST', 'Internal Audit', 'Statutory Audit', 'Bank Audit'
];

const EditJobModal = ({ job, onClose, onSave }) => {
    const jobId = job._id || job.id;
    const [formData, setFormData] = useState({
        number_of_positions: job.number_of_positions || '',
        about_job: job.about_job || '',
        stipend_from: job.stipend_from || '',
        stipend_to: job.stipend_to || '',
        resume_required: typeof job.resume_required === 'boolean' ? job.resume_required : true,
        areas_of_articleship: Array.isArray(job.areas_of_articleship) ? job.areas_of_articleship : [],
        article_preference: job.article_preference || 'any'
    });
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setFormData({
            number_of_positions: job.number_of_positions || '',
            about_job: job.about_job || '',
            stipend_from: job.stipend_from || '',
            stipend_to: job.stipend_to || '',
            resume_required: typeof job.resume_required === 'boolean' ? job.resume_required : true,
            areas_of_articleship: Array.isArray(job.areas_of_articleship) ? job.areas_of_articleship : [],
            article_preference: job.article_preference || 'any'
        });
        setError('');
    }, [job]);

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const toggleArea = (area) => {
        setFormData(prev => {
            const exists = prev.areas_of_articleship.includes(area);
            const nextAreas = exists
                ? prev.areas_of_articleship.filter(a => a !== area)
                : [...prev.areas_of_articleship, area];
            return { ...prev, areas_of_articleship: nextAreas };
        });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.number_of_positions || parseInt(formData.number_of_positions, 10) < 1) {
            setError('Provide number of positions');
            return;
        }
        if (!formData.about_job.trim()) {
            setError('Describe the job');
            return;
        }
        if (!formData.stipend_from || !formData.stipend_to) {
            setError('Enter stipend range');
            return;
        }
        if (parseInt(formData.stipend_from, 10) > parseInt(formData.stipend_to, 10)) {
            setError('"From" should be less than "To"');
            return;
        }

        setIsSaving(true);

        const payload = {
            number_of_positions: parseInt(formData.number_of_positions, 10),
            about_job: formData.about_job,
            stipend_from: parseInt(formData.stipend_from, 10),
            stipend_to: parseInt(formData.stipend_to, 10),
            resume_required: Boolean(formData.resume_required),
            areas_of_articleship: formData.areas_of_articleship,
            article_preference: formData.article_preference
        };

        try {
            const response = await apiCall.put(`/jobs/${jobId}`, JSON.stringify(payload));
            const updatedJob = response.data || response || { ...job, ...payload };
            onSave(updatedJob);
        } catch (err) {
            console.error('Error updating job:', err);
            setError('Failed to update job. Try again.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fj-modal-overlay" onClick={onClose}>
            <div className="fj-modal" onClick={(e) => e.stopPropagation()}>
                <h3>Edit Articleship</h3>

                <div className="fj-modal-row fj-modal-row-top">
                    <div className="fj-modal-field fj-modal-field-small">
                        <label>Positions</label>
                        <input
                            type="number"
                            min="1"
                            value={formData.number_of_positions}
                            onChange={(e) => handleFieldChange('number_of_positions', e.target.value)}
                        />
                    </div>
                </div>

                <div className="fj-modal-row fj-modal-row-stipend">
                    <div className="fj-modal-field fj-modal-field-small">
                        <label>Stipend From (₹)</label>
                        <input
                            type="number"
                            min="0"
                            value={formData.stipend_from}
                            onChange={(e) => handleFieldChange('stipend_from', e.target.value)}
                        />
                    </div>
                    <div className="fj-modal-field fj-modal-field-small">
                        <label>Stipend To (₹)</label>
                        <input
                            type="number"
                            min="0"
                            value={formData.stipend_to}
                            onChange={(e) => handleFieldChange('stipend_to', e.target.value)}
                        />
                    </div>
                </div>

                <div className="fj-modal-field">
                    <label>About the Job</label>
                    <textarea
                        rows={3}
                        value={formData.about_job}
                        onChange={(e) => handleFieldChange('about_job', e.target.value)}
                    />
                </div>

                <div className="fj-modal-field">
                    <label>Resume</label>
                    <div className="fj-modal-toggle-row">
                        <button
                            type="button"
                            className={`fj-modal-toggle-btn ${formData.resume_required ? 'active' : ''}`}
                            onClick={() => handleFieldChange('resume_required', true)}
                        >
                            Mandatory
                        </button>
                        <button
                            type="button"
                            className={`fj-modal-toggle-btn ${!formData.resume_required ? 'active' : ''}`}
                            onClick={() => handleFieldChange('resume_required', false)}
                        >
                            Optional
                        </button>
                    </div>
                </div>
                <div className="fj-modal-field">
                    <label>Article Preference</label>
                    <div className="fj-modal-toggle-row">
                        {[{ value: 'any', label: 'Any' }, { value: 'local', label: 'Local' }, { value: 'immigrant', label: 'Immigrant' }].map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`fj-modal-toggle-btn ${formData.article_preference === opt.value ? 'active' : ''}`}
                                onClick={() => handleFieldChange('article_preference', opt.value)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="fj-modal-field">
                    <label>Areas of Articleship</label>
                    <div className="fj-modal-areas">
                        {AREA_OPTIONS.map(area => (
                            <label key={area} className="fj-modal-checkbox">
                                <input
                                    type="checkbox"
                                    checked={(formData.areas_of_articleship || []).includes(area)}
                                    onChange={() => toggleArea(area)}
                                />
                                <span>{area}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {error && <div className="fj-modal-error">{error}</div>}

                <div className="fj-modal-actions">
                    <button className="fj-btn fj-btn-secondary" onClick={onClose} type="button">
                        Cancel
                    </button>
                    <button
                        className="fj-btn fj-btn-primary"
                        onClick={handleSave}
                        type="button"
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ResumeViewer = ({ url, onClose }) => {
    if (!url) return null;

    const isAndroid = typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent);
    const viewerUrl = isAndroid
        ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`
        : `${url}#toolbar=1&navpanes=0`;

    return (
        <div className="fj-resume-viewer-overlay" onClick={onClose}>
            <div className="fj-resume-viewer" onClick={(e) => e.stopPropagation()}>
                <div className="fj-resume-viewer-header">
                    <span className="fj-resume-viewer-title">Resume</span>
                    <button className="fj-resume-viewer-close" onClick={onClose}>×</button>
                </div>
                <div className="fj-resume-viewer-body">
                    <iframe
                        src={viewerUrl}
                        title="Resume"
                        className="fj-resume-iframe"
                    />
                </div>
                <div className="fj-resume-viewer-footer">
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="fj-resume-open-external"
                    >
                        Open in new tab
                    </a>
                </div>
            </div>
        </div>
    );
};

const STATUS_OPTIONS = [
    { value: 'in_review', label: 'Under Review' },
    { value: 'accepted', label: 'Accept' },
    { value: 'rejected', label: 'Reject' }
];

const getApplicantUserId = (applicant) => {
    return applicant?.user_id || applicant?.userId || applicant?.user?.id || applicant?.user?._id || null;
};

const StatusDropdown = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const current = STATUS_OPTIONS.find(o => o.value === value) || STATUS_OPTIONS[0];

    return (
        <div className="fj-custom-select" onClick={(e) => e.stopPropagation()}>
            <button
                type="button"
                className="fj-custom-select-trigger"
                onClick={() => setOpen(prev => !prev)}
            >
                <span>{current.label}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {open && (
                <>
                    <div className="fj-custom-select-backdrop" onClick={() => setOpen(false)} />
                    <div className="fj-custom-select-menu">
                        {STATUS_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                type="button"
                                className={`fj-custom-select-option ${opt.value === value ? 'fj-custom-select-option--selected' : ''}`}
                                onClick={() => { onChange(opt.value); setOpen(false); }}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const ApplicantsPage = ({ job, onBack }) => {
    const [applicants, setApplicants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewingResumeUrl, setViewingResumeUrl] = useState(null);
    const [firmId, setFirmId] = useState(null);
    const [statusMap, setStatusMap] = useState({});
    const handleResumeButtonClick = (e, url) => {
        e.stopPropagation();
        setViewingResumeUrl(url);
    };

    const jobId = job._id || job.id;

    useEffect(() => {
        fetchFirmInfo();
    }, [jobId]);

    const fetchFirmInfo = async () => {
        let resolvedFirmId = null;
        try {
            const response = await apiCall.get('/firm/me');
            const firm = response.data?.data || response.data || response;
            resolvedFirmId = firm?.firm_id || firm?._id || firm?.firmId || null;
            setFirmId(resolvedFirmId);
        } catch (error) {
            console.error('Error fetching firm info:', error);
        } finally {
            fetchApplicants(resolvedFirmId);
        }
    };

    const fetchApplicants = async (resolvedFirmId = firmId) => {
        try {
            setLoading(true);
            const response = await apiCall.get(`/jobs/${jobId}/applicants`);
            const data = response.data || response || [];
            const list = Array.isArray(data) ? data : [];
            setApplicants(list);
            list.forEach(applicant => {
                const uid = getApplicantUserId(applicant);
                if (uid) {
                    fetchApplicantStatus(uid, resolvedFirmId);
                }
            });
        } catch (error) {
            console.error('Error fetching applicants:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchApplicantStatus = async (userId, resolvedFirmId = firmId) => {
        if (!resolvedFirmId || !userId) return;
        try {
            const response = await apiCall.get(`/jobs/firms/${resolvedFirmId}/users/${userId}/status`);
            console.log('Status response for user', userId, ':', response);
            const result = response.data?.data || response.data || response;
            const status = result?.status || result?.application_status;
            if (status) {
                setStatusMap(prev => ({ ...prev, [userId]: status }));
            }
        } catch (error) {
            console.error('Error fetching applicant status:', error);
        }
    };

    const updateApplicantStatus = async (userId, status) => {
        if (!firmId) return;
        try {
            await apiCall.post(`/jobs/${jobId}/applications/status`, JSON.stringify({
                job_id: jobId,
                firm_id: firmId,
                user_id: userId,
                status
            }));
            setStatusMap(prev => ({ ...prev, [userId]: status }));
        } catch (error) {
            console.error('Error updating applicant status:', error);
        }
    };

    return (
        <div className="fj-container">
            <div className="fj-header fj-header-with-back">
                <button className="fj-back-btn" onClick={onBack}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                </button>
                <div className="fj-header-text">
                    <h1>Applicants</h1>
                    <p className="fj-header-sub">
                        {job.number_of_positions} position{job.number_of_positions !== 1 ? 's' : ''} · ₹{job.stipend_from?.toLocaleString()} - ₹{job.stipend_to?.toLocaleString()}/mo
                    </p>
                </div>
            </div>

            <div className="fj-content">
                {loading ? (
                    <div className="fj-loading">Loading applicants...</div>
                ) : applicants.length === 0 ? (
                    <div className="fj-empty">
                        <p>No applications yet</p>
                        <p className="fj-empty-hint">Applicants will appear here when users apply</p>
                    </div>
                ) : (
                    <div className="fj-applicants-page-list">
                        <div className="fj-applicants-count">{applicants.length} applicant{applicants.length !== 1 ? 's' : ''}</div>
                        {applicants.map((applicant, idx) => {
                            const realUserId = getApplicantUserId(applicant);
                            const userKey = realUserId || applicant._id || `applicant-${idx}`;
                            return (
                                <div key={userKey} className="fj-applicant-card">
                                    <div className="fj-applicant-top">
                                        <div className="fj-applicant-avatar">
                                            {(applicant.first_name || 'A').charAt(0).toUpperCase()}
                                        </div>
                                        <div className="fj-applicant-info">
                                            <div className="fj-applicant-name">
                                                {applicant.first_name} {applicant.last_name}
                                            </div>
                                            <div className="fj-applicant-contact">
                                                {applicant.email}
                                            </div>
                                            {applicant.number && (
                                                <div className="fj-applicant-phone">{applicant.number}</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="fj-applicant-bottom">
                                        {applicant.resume_url && (
                                            <button
                                                type="button"
                                                className="fj-resume-btn"
                                                onClick={(e) => handleResumeButtonClick(e, applicant.resume_url)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                    <polyline points="14 2 14 8 20 8"></polyline>
                                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                                </svg>
                                                Resume
                                            </button>
                                        )}
                                        <StatusDropdown
                                            value={statusMap[realUserId] || applicant.status || applicant.application_status || 'in_review'}
                                            onChange={(val) => realUserId && updateApplicantStatus(realUserId, val)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {viewingResumeUrl && (
                <ResumeViewer url={viewingResumeUrl} onClose={() => setViewingResumeUrl(null)} />
            )}
        </div>
    );
};

const FirmJobs = () => {
    const [postedJobs, setPostedJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});
    const [editingJob, setEditingJob] = useState(null);

    useEffect(() => {
        fetchPostedJobs();
    }, []);

    const fetchPostedJobs = async () => {
        try {
            const response = await apiCall.get('/jobs/firm');
            const data = response.data || response || [];
            setPostedJobs(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching posted jobs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteJob = async (e, jobId) => {
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to delete this job?')) return;
        try {
            await apiCall.delete(`/jobs/${jobId}`);
            setPostedJobs(prev => prev.filter(j => (j._id || j.id) !== jobId));
        } catch (error) {
            console.error('Error deleting job:', error);
        }
    };

    if (selectedJob) {
        return <ApplicantsPage job={selectedJob} onBack={() => setSelectedJob(null)} />;
    }

    return (
        <div className="fj-container">
            <div className="fj-header">
                <h1>Your Articleships</h1>
            </div>

            <div className="fj-content">
                {loading ? (
                    <div className="fj-loading">Loading jobs...</div>
                ) : postedJobs.length === 0 ? (
                    <div className="fj-empty">
                        <p>No jobs posted yet</p>
                        <p className="fj-empty-hint">Post your first articleship opening</p>
                    </div>
                ) : (
                    <div className="fj-jobs-list">
                        {postedJobs.map((job, idx) => {
                            const jobId = job._id || job.id || idx;
                            return (
                                <div key={jobId} className="fj-job-card" onClick={() => setSelectedJob(job)}>
                                    <div className="fj-job-title-row">
                                        <h3 className="fj-job-title">
                                            {job.number_of_positions} Position{job.number_of_positions !== 1 ? 's' : ''} — {(job.areas_of_articleship || [])[0] || 'Articleship'}
                                        </h3>
                                        <span className="fj-job-stipend">
                                            ₹{job.stipend_from?.toLocaleString()} - ₹{job.stipend_to?.toLocaleString()}/month
                                        </span>
                                    </div>

                                    {job.about_job && (
                                        <div className="fj-job-desc-wrap">
                                            <p className={`fj-job-desc ${expandedDescriptions[jobId] ? 'fj-desc-expanded' : 'fj-desc-collapsed'}`}>
                                                {job.about_job}
                                            </p>
                                            {job.about_job.length > 150 && (
                                                <button
                                                    className="fj-desc-toggle"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedDescriptions(prev => ({
                                                            ...prev,
                                                            [jobId]: !prev[jobId]
                                                        }));
                                                    }}
                                                >
                                                    {expandedDescriptions[jobId] ? 'Show less' : 'More'}
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    <div className="fj-job-tags">
                                        {(job.areas_of_articleship || []).map((area, i) => (
                                            <span key={i} className="fj-tag">{area}</span>
                                        ))}
                                        {job.resume_required && (
                                            <span className="fj-tag fj-tag-resume">Resume Required</span>
                                        )}
                                    </div>

                                    <div className="fj-job-bottom">
                                        <div className="fj-job-actions-left">
                                            <button
                                                className="fj-edit-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditingJob(job);
                                                }}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="fj-delete-btn"
                                                onClick={(e) => handleDeleteJob(e, jobId)}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                        <span className="fj-view-applicants">View Applicants →</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {editingJob && (
                <EditJobModal
                    job={editingJob}
                    onClose={() => setEditingJob(null)}
                    onSave={(updatedJob) => {
                        setPostedJobs(prev => prev.map(j => {
                            const id = j._id || j.id;
                            if (id === (editingJob._id || editingJob.id)) {
                                return updatedJob;
                            }
                            return j;
                        }));
                        setEditingJob(null);
                    }}
                />
            )}
        </div>
    );
};

export default FirmJobs;
